import { isAgentChatInboxItem } from '@/lib/inbox-thread-filters'
import type { InboxItem } from '@/hooks/use-inbox'
import type { Thread } from './types'
import type { InboxFolderId, InboxListFocus, InboxSortDir } from './inbox-folder-types'
import { isInboxThreadVisible } from './mappers'

/** ADR-0015: agent_chat threads belong in Chats, not Inbox triage. */
export function isInboxTriageThread(thread: Thread): boolean {
  const kind = thread.metadata?.threadKind
  if (kind === 'agent_chat') return false
  if (thread.kind === 'chat') return false
  return true
}

export function isInboxTriageItem(item: InboxItem): boolean {
  return !isAgentChatInboxItem(item)
}

type FilterOpts = {
  activeWorkspaceIds: string[]
  activeExtension?: string | null
  searchQuery?: string
  /** Unread chip on ExtensionFilterBar. Ignored for archived / snoozed folders. */
  listFocus?: InboxListFocus
}

function matchesSearch(thread: Thread, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    thread.title.toLowerCase().includes(q) ||
    thread.preview.toLowerCase().includes(q) ||
    thread.source.toLowerCase().includes(q)
  )
}

/** Client-side folder filter over mapped Thread rows. */
export function filterThreadsByFolder(
  threads: Thread[],
  folderId: InboxFolderId,
  opts: FilterOpts,
): Thread[] {
  const {
    activeWorkspaceIds,
    activeExtension,
    searchQuery = '',
    listFocus = 'all',
  } = opts

  // Legacy unread folder id = inbox noun + unread focus
  const effectiveFolder = folderId === 'unread' ? 'inbox' : folderId
  const effectiveFocus: InboxListFocus =
    folderId === 'unread' ? 'unread' : listFocus

  return threads.filter((t) => {
    if (!matchesSearch(t, searchQuery)) return false

    // ADR-0015: Chats is its own surface. Do not apply triage exclusion here —
    // isInboxTriageThread() returns false for kind === 'chat', which emptied this folder.
    if (effectiveFolder === 'chats') {
      if (t.kind !== 'chat') return false
      if (t.status === 'archived' || t.status === 'snoozed') return false
      if (effectiveFocus === 'unread' && t.status !== 'unread') return false
      return isInboxThreadVisible(t, activeWorkspaceIds)
    }

    if (!isInboxTriageThread(t)) return false

    if (effectiveFolder === 'archived') {
      return t.status === 'archived' && isInboxThreadVisible(t, activeWorkspaceIds)
    }

    if (!isInboxThreadVisible(t, activeWorkspaceIds)) return false

    if (effectiveFolder === 'snoozed') return t.status === 'snoozed'
    if (t.status === 'archived' || t.status === 'snoozed') return false

    if (effectiveFocus === 'unread' && t.status !== 'unread') return false

    switch (effectiveFolder) {
      case 'inbox':
        return true
      case 'flows':
        return t.kind === 'flow'
      case 'suggestions':
        return t.kind === 'suggestion' || t.kind === 'invite'
      case 'voice':
        return t.kind === 'voice'
      case 'email':
        return t.kind === 'email'
      case 'extensions':
        if (t.kind !== 'extension') return false
        if (activeExtension && t.extensionId !== activeExtension) return false
        return true
      default:
        return true
    }
  })
}

/** Sort inbox threads by receivedAt (Newest default). */
export function sortInboxThreads(threads: Thread[], dir: InboxSortDir): Thread[] {
  const sorted = [...threads]
  sorted.sort((a, b) => {
    const ta = new Date(a.receivedAt).getTime()
    const tb = new Date(b.receivedAt).getTime()
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0
    if (Number.isNaN(ta)) return 1
    if (Number.isNaN(tb)) return -1
    return dir === 'oldest' ? ta - tb : tb - ta
  })
  return sorted
}

/** Count threads visible in a folder (workspace-scoped). */
export function countThreadsForFolder(
  threads: Thread[],
  folderId: InboxFolderId,
  activeWorkspaceIds: string[],
): number {
  return filterThreadsByFolder(threads, folderId, { activeWorkspaceIds }).length
}

/** Unread badge count for a folder (non-archived triage only). */
export function unreadCountForFolder(
  threads: Thread[],
  folderId: InboxFolderId,
  activeWorkspaceIds: string[],
): number {
  return filterThreadsByFolder(threads, folderId, { activeWorkspaceIds }).filter(
    (t) => t.status === 'unread',
  ).length
}
